import { Injectable } from '@angular/core';
import { Control, ControlType, DateRange, KngFilterPanelComponent, KngFilterState, Section } from 'kinetic-angular';
import { DateConverterService } from '../date-converter/date-converter.service';
import { DateFormat, FilterOptions } from './shared-enums';
import { KeyValuePair, LocationAddress } from './shared-interface';
import { SharedService } from './shared.service';
import { BillingServiceFilterAddress } from '../../components/service-management/components/service-management-page/sub-modules/service-order-detail/definitions/service-order-detail.interface';

@Injectable({
	providedIn: 'root'
})
export class SharedFilterService 
{
	constructor(public dateService: DateConverterService, public sharedService: SharedService) { }

	public checkIfControlIsMarkedAsSelected(control: Control): boolean 
	{
		let checkRes: boolean = false;
		if (control.options.length > 0) 
		{
			control.options.forEach((opt) => 
			{
				if (opt.checked === true) 
				{
					checkRes = true;
				}
			});
		}
		return ((control.type === ControlType.checkbox && control.value === true) || checkRes);
	}
	public filterHasAtLeastOneSelectedControl(filterState: KngFilterState): boolean 
	{
		const sectionWithSelectedControl: Section | undefined = filterState.filterData.sections.find((section: Section) =>
			section.controls.find((control: Control) => this.checkIfControlIsMarkedAsSelected(control)));
		return sectionWithSelectedControl !== undefined;
	}
	public getCountOfFilterPanel(control: Control[], label: string, sections: Section[], filterComponent: KngFilterPanelComponent | undefined)
  	{
		const selectedOptions: string[] = [];
		control.forEach((ctrl) => 
		{
			if (ctrl?.type === ControlType.checkbox) 
			{
				if (ctrl.value === true)
					selectedOptions.push(ctrl.id);
				else if (ctrl.value === false && selectedOptions.length > 0)
					this.removeFromSelectedOption(selectedOptions, ctrl.id);
			}
			else if (ctrl?.type === ControlType.notDate) 
			{
				const option = control[0].options;
				option.forEach((opt) => 
				{
					if (opt.checked === true)
						selectedOptions.push(opt.label);
					else if (opt.checked === false)
						this.removeFromSelectedOption(selectedOptions, opt.label);
				});
			}
		});
		return this.setCountOfFilterPanel(selectedOptions, label, sections, filterComponent);
	}
	public removeFromSelectedOption(selectedOptions: string[], label: string) 
	{
		const index = selectedOptions.indexOf(label);
		if (index !== -1)
			selectedOptions.splice(index, 1);
	}
	public setCountOfFilterPanel(selectedOptions: string[], label: string, sections: Section[], filterComponent: KngFilterPanelComponent | undefined)
	{
		if (filterComponent) 
		{
			sections.forEach((sec: Section) => 
			{
				if (label === sec.label)
					sec.selectedValue = selectedOptions;
			});
		}
		return sections;
	}
	addSelectedFilter(label: string, type: string, selectedFilterObj: KeyValuePair[], value?: string, id?: string) 
	{
		if (label === DateRange.custom && value) 
		{
			const dateRange = value.split('@');
			const from = this.dateService.tranformDate(dateRange[0], DateFormat.default);
			const to = this.dateService.tranformDate(dateRange[1], DateFormat.default);
			const contactToFrom = from + ' - ' + to;
			selectedFilterObj.push({ key: type, value: contactToFrom, id: '' });
		}
		else if (label === DateRange.lastSixtyDays || label === DateRange.nextThirtyDays)
			selectedFilterObj.push({ key: type, value: label, id: '' });
		else
			this.checkForPreviousYears(label, type, selectedFilterObj, id);
		this.mapSelectedFilter(selectedFilterObj);
		return selectedFilterObj;
	}
	checkForPreviousYears(label: string, type: string, selectedFilterObj: KeyValuePair[], id?: string)
	{
		if (label === DateRange.lastYear)
			selectedFilterObj.push({ key: type, value: DateRange.lastYear, id: '', disabled: true });
		else if(label === DateRange.last2YearsDefault)
			selectedFilterObj.push({ key: type, value: DateRange.last2YearsDefault, id: '', disabled: true });
		else
			selectedFilterObj.push({ key: type, value: label, id: id ?? '' });
	}
	mapSelectedFilter(selectedFilterObj: KeyValuePair[]) 
	{
		selectedFilterObj.forEach(obj => 
		{
			if (obj.key === FilterOptions.dateRange && (obj.value === DateRange.lastSixtyDays || obj.value === DateRange.lastYearDefault || obj.value === DateRange.last2YearsDefault || obj.value === DateRange.nextThirtyDays))
				obj.disabled = true;
			else
				obj.disabled = false;
		});
	}
	public async findBillingServiceLocation(searchKey: string, existingControls: Control[], searchFullAddress?: boolean): Promise<Control[]>
	{
		const locationControls = existingControls;
		if (searchKey) 
		{
			let filteredResult: Control[] = [];
			if (searchFullAddress)
			{
				filteredResult = locationControls.filter((location: Control) =>
					location.type === ControlType.checkbox && location.address &&
					this.sharedService.formatAddress(location.address, searchFullAddress)?.toLowerCase().includes(String(searchKey)?.toLowerCase())
					|| location.label?.toLowerCase().includes(String(searchKey)?.toLowerCase())
				);
			}
			else
			{
				filteredResult = locationControls.filter((location: Control) =>
					location.type === ControlType.checkbox &&
					location.label.toLowerCase().includes(String(searchKey)?.toLowerCase()));
			}	
			return Promise.resolve<Control[]>(filteredResult);
		}
		else
			return Promise.resolve<Control[]>([]);
	}

	public async findAssignedLocation(searchKey: string, existingControls: Control[], searchFullAddress?: boolean): Promise<Control[]> 
	{
		const locationControls = existingControls;
		if (searchKey) 
		{
			let filteredResult: Control[] = [];
			if (searchFullAddress)
			{
				filteredResult = locationControls.filter((location: Control) =>
					location.type === ControlType.checkbox && location.address &&
					this.sharedService.formatAddress(location.address, searchFullAddress)?.toLowerCase().includes(String(searchKey)?.toLowerCase())
					|| location.label?.toLowerCase().includes(String(searchKey)?.toLowerCase()) || location.id.includes(String(searchKey)?.toLowerCase())
				);
			}
			else
			{
				filteredResult = locationControls.filter((location: Control) =>
					location.type === ControlType.checkbox &&
					location.label?.toLowerCase().includes(String(searchKey)?.toLowerCase()));
			}	
			return Promise.resolve<Control[]>(filteredResult);
		}
		else
			return Promise.resolve<Control[]>([]);
	}
	removeFromSelectedFilter(filterValue: string, filter: string[] | LocationAddress[], selectedFilterList: KeyValuePair[]) 
	{
		selectedFilterList.forEach((obj, index) => 
		{
			if (obj.id === filterValue && filter)
				selectedFilterList.splice(index, 1);
		});
		return selectedFilterList;
	}
	removeSearchtextfromInputAfterClear(payload: string[] | LocationAddress[], key: string, sections: Section[]) 
	{
		if (payload.length === 0) 
		{
			sections.forEach((section: Section) => 
			{
				if (section.label === key) 
				{
					section.controls.forEach((control: Control) => 
					{
						if (control.type === ControlType.input)
							control.setValue('');
					});
				}
			});
		}
		return sections;
	}
	clearAllFilterPanel(sections: Section[]) 
	{
		sections.forEach((section: Section) => 
		{
			section.selectedValue = [];
			section.controls.forEach((control: Control) => 
			{
				if (control.type === ControlType.input)
					control.setValue('');
				else if (control.type === ControlType.checkbox)
					control.setValue(false);
				else if (control.type === ControlType.radio || control.type === ControlType.notDate)
					control.options.forEach((opt) => opt.checked && (opt.checked = false));
			});
		});
		return sections;
	}
	updateAllCheckboxValues(selectedFilters: Control[], value: boolean) 
	{
		selectedFilters.forEach((control: Control) => 
		{
			if (control.type === ControlType.checkbox)
				control.setValue(value);
		});
		return selectedFilters;
	}
	getFilterControls(controls: Control[]): Control[] 
	{
		const selectedLocations: Control[] = [];
		controls?.forEach(location => 
		{
			if (location.value)
				selectedLocations.push(location);
		});
		controls = this.sharedService.getLocationValues();
		controls?.forEach(location => 
		{
			selectedLocations.forEach(selectedLocation => 
			{
				if (selectedLocation.id === location.id)
					location.setValue(true);
			});
		});
		return controls;
	}

	getShippingBillingLocationFilterControls(controls: Control[], shippingLocations: BillingServiceFilterAddress[]): Control[] 
	{
		const selectedShippingLocation: Control[] = [];
		controls.forEach(address => 
		{
			if (address.value)
				selectedShippingLocation.push(address);
		});
		controls = this.getShippingLocationValues(shippingLocations);
		controls.forEach(address => 
		{
			selectedShippingLocation.forEach(element => 
			{
				if (element.id === address.id)
					address.setValue(true);
			});
		});
		return controls;
	}

	getShippingLocationValues(shippingLocations: BillingServiceFilterAddress[]): Control[]
	{
		const assignedShippingLocation: Control[] = [];
		shippingLocations?.forEach((element) =>
		{
			const address = {
				addressLine1: element.address.addressLine1,
				city: element.address.city,
				state: element.address.state,
				zipCode: element.address.zip
			};
			const data = { name: element.locationLabel, id: element.locationId, address };
			assignedShippingLocation.push(this.sharedService.initialiseFilterControl(data.id, data.name, ControlType.checkbox, data.address));
		});
		return assignedShippingLocation;
	}
	public async findEquipmentRepairType(searchKey: string, equipmentControls: Control[]): Promise<Control[]> 
	{
		if (searchKey) 
		{
			const filteredResult: Control[] = equipmentControls.filter((item: Control) =>
				item.type === ControlType.checkbox && item.label.toLowerCase().includes(String(searchKey)?.trimLeft()?.toLowerCase()));
			return Promise.resolve<Control[]>(filteredResult);
		}
		else 
		{
			const filteredResult: Control[] = equipmentControls.filter((item: Control) => item.type === ControlType.checkbox);
			return Promise.resolve<Control[]>(filteredResult);
		}
	}
	updateFilterSection(sectionControls: Control[], selectedOptions: string[] | undefined) 
	{
		if (selectedOptions && selectedOptions.length > 0) 
		{
			sectionControls.forEach((control) => 
			{
				if (control.type === ControlType.checkbox)
					(selectedOptions.includes(control.id) || selectedOptions.includes(control.label) || selectedOptions.includes(control.label.toLowerCase())) ? control.setValue(true) : control.setValue(false);
			});
		}
		else 
		{
			sectionControls.forEach((control) => 
			{
				if (control.type === ControlType.checkbox)
					control.setValue(false);
			});
		}
	}
	public updateFilterPanelOptions(newOptions: Control[], oldOptions: Control[], filterComponent: KngFilterPanelComponent | undefined, filterState: KngFilterState): void 
	{
		oldOptions.splice(1, oldOptions.length - 1, ...newOptions);
		filterComponent ? filterComponent.updateState(filterState) : (() => { })();
	}
	setDateFilterIdCheck(section: Section, value: string, valueId: string) 
	{
		section.selectedValue = [value];
		section.controls[0].options.forEach((opt) => opt.checked = false);
		const index = section.controls[0].options.findIndex(opt => opt.id === valueId);
		if (index >= 0)
			section.controls[0].options[index].checked = true;
		return section;
	}
	setDateFilterLabelCheck(section: Section, value: string) 
	{
		section.selectedValue = [value];
		section.controls[0].options.forEach((opt) => opt.checked = false);
		const index = section.controls[0].options.findIndex(opt => opt.label === value);
		if (index >= 0)
			section.controls[0].options[index].checked = true;
		return section;
	}
	setDateFilterCloseFilter(selectedFilterObj: KeyValuePair[], section: Section, filterComponent: KngFilterPanelComponent | undefined)
	{
		selectedFilterObj.forEach(obj => 
		{
			if (filterComponent)
				filterComponent.showToFromInput = (obj.value.includes('-')) || false;

			if (obj.key === FilterOptions.dateRange) 
			{
				if (obj.value.includes('-')) 
				{
					section.controls[0].options.forEach((opt) => opt.checked = false);
					const index = section.controls[0].options.length - 1;
					section.controls[0].options[index].checked = true;
					section.selectedValue = [DateRange.custom];
				}
				else
					section = this.setDateFilterLabelCheck(section, obj.value);
			}
		});
		return section;
	}
	public async findSearchedAddress(searchKey: string, existingControls: Control[]): Promise<Control[]>
	{
		const addressControls = existingControls;
		if (searchKey)
		{
			const filteredResult: Control[] = addressControls.filter((item: Control) =>
			{
				const valuesToCheck = [
					item.address?.addressLine1,
					item.address?.addressLine2,
					item.address?.city,
					item.address?.state,
					item.address?.zipCode
				];
				
				const addressString = valuesToCheck.filter(value => value !== undefined && value !== null).join(', ').toLowerCase(); return addressString.includes(String(searchKey)?.toLowerCase());
			});
			return Promise.resolve<Control[]>(filteredResult);
		}
		else
			return Promise.resolve<Control[]>([]);
	}
	getSelectedCheckbox(controls: Control[])
	{
		return controls.filter(control => control.type === ControlType.checkbox && control.value === true);
	}
	getCheckboxIds(controls: Control[])
	{
		return controls.map(control=>control?.id);
	}
	getSelectedCheckboxByIds(controls: Control[])
	{
		return this.getCheckboxIds(this.getSelectedCheckbox(controls));
	}
	setfilterCollapsedState(sections: Section[], collapsed: boolean)
	{
		sections.forEach((section)=>section.collapsed = collapsed);
		return sections;
	}
}
